import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileUpload } from '@/components/shared/FileUpload';
import { FormSection } from '@/components/shared/FormSection';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import type { Tenant } from '@packages/shared-types';
import {
  getTenantById,
  type UpdateTenantPayload,
  updateTenant,
  uploadTenantImage,
  deleteTenant,
} from '@/services/tenantService';

const tenantFormSchema = z.object({
  name: z.string().min(1, 'pages.tenants.form.nameError'),
  subdomain: z.string().optional(),
  industry: z.string().optional(),
  language: z.enum(['en', 'es']).optional(),
  foldersEnabled: z.boolean().optional(),
});

type TenantFormValues = z.infer<typeof tenantFormSchema>;

const LANGUAGE_OPTIONS = [
  { value: 'en', labelKey: 'pages.tenants.form.languageOptions.en' as const },
  { value: 'es', labelKey: 'pages.tenants.form.languageOptions.es' as const },
] as const;

const INDUSTRY_OPTIONS = [
  { value: 'education', label: 'Education' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'retail', label: 'Retail' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'library', label: 'Library' },
  { value: 'healthcare', label: 'Healthcare' },
] as const;

export function EditTenantPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: {
      name: '',
      subdomain: '',
      industry: '',
      language: 'en',
      foldersEnabled: false,
    },
  });

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const loadTenant = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getTenantById(id);
      setTenant(data);
      setProfileImageUrl(data.profileImage ?? null);
      reset({
        name: data.name,
        subdomain: data.subdomain ?? '',
        industry: data.industry ?? '',
        language: (data.language === 'es' ? 'es' : 'en') as 'en' | 'es',
        foldersEnabled:
          (data.settings as { foldersEnabled?: boolean } | undefined)
            ?.foldersEnabled ?? false,
      });
      setSelectedFile(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('pages.tenants.error.load'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, t]);

  useEffect(() => {
    void loadTenant();
  }, [loadTenant]);

  const handleSelectFile = useCallback((file: File | null) => {
    setSelectedFile(file);
  }, []);

  const handleSubmitTenant = async (values: TenantFormValues): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateTenantPayload = {
      name: values.name,
      subdomain: values.subdomain || undefined,
      industry: values.industry || undefined,
      language: values.language,
      settings: {
        ...(tenant?.settings as Record<string, unknown>),
        foldersEnabled: values.foldersEnabled ?? false,
      },
    };

    try {
      await updateTenant(id, payload);
      if (selectedFile) {
        await uploadTenantImage(id, selectedFile);
      }
      await loadTenant();
      setSelectedFile(null);
      toast.success(t('pages.tenants.edit.success'));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('pages.tenants.error.update');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !tenant) return;
    setIsDeleting(true);
    try {
      await deleteTenant(id);
      toast.success(t('pages.tenants.actions.deleteSuccess'));
      navigate('/tenants');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t('pages.tenants.error.delete');
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitTenant)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {t('pages.tenants.error.notFound')}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={t('pages.tenants.edit.title')}
      description={
        tenant
          ? t('pages.tenants.edit.description', { name: tenant.name })
          : undefined
      }
      isLoading={isLoading}
      error={error}
      formId="tenant-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.tenants.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitTenant)()}
      saveLabel={t('pages.tenants.actions.update')}
      savingLabel={t('pages.tenants.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.tenants.actions.delete')}
      deletingLabel={t('pages.tenants.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={t('pages.tenants.dialogs.delete.title')}
      deleteDialogDescription={t('pages.tenants.dialogs.delete.description', {
        name: tenant?.name,
      })}
      entityExists={!!tenant}
    >
      <FormSection
        title={t('pages.tenants.edit.imageLabel')}
        description={t('pages.tenants.edit.imageHelper')}
      >
        <FileUpload
          currentImageUrl={profileImageUrl}
          onFileChange={handleSelectFile}
          disabled={isSaving}
          showLabel={false}
        />
      </FormSection>
      <form
        id="tenant-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitTenant)}
      >
        <FormSection
          title={t('pages.tenants.form.name')}
          description={t('pages.tenants.form.namePlaceholder')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="tenant-name"
            {...register('name')}
            placeholder={t('pages.tenants.form.namePlaceholder')}
            disabled={isSaving}
          />
        </FormSection>
        {/* Subdomain field hidden for now */}
        {/* <FormSection
          title={t('pages.tenants.form.subdomain')}
          description={t('pages.tenants.form.subdomainPlaceholder')}
          error={errors.subdomain ? errors.subdomain.message : null}
        >
          <Input
            id="tenant-subdomain"
            {...register('subdomain')}
            placeholder={t('pages.tenants.form.subdomainPlaceholder')}
            disabled={isSaving}
          />
        </FormSection> */}
        <FormSection
          title={t('pages.tenants.form.industry')}
          description={t('pages.tenants.form.industryPlaceholder')}
          error={errors.industry ? errors.industry.message : null}
        >
          <Controller
            name="industry"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={(value) => field.onChange(value || undefined)}
                disabled={isSaving}
              >
                <SelectTrigger id="tenant-industry">
                  <SelectValue placeholder={t('pages.tenants.form.industryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(`pages.tenants.form.industryOptions.${option.value}`, { defaultValue: option.label })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.tenants.form.language')}
          description={t('pages.tenants.form.languagePlaceholder')}
        >
          <Controller
            name="language"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || 'en'}
                onValueChange={(value) => field.onChange(value as 'en' | 'es')}
                disabled={isSaving}
              >
                <SelectTrigger id="tenant-language">
                  <SelectValue placeholder={t('pages.tenants.form.languagePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>
        <FormSection
          title={t('pages.tenants.form.foldersEnabled')}
          description={t('pages.tenants.form.foldersEnabledDescription')}
        >
          <Controller
            name="foldersEnabled"
            control={control}
            render={({ field }) => (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tenant-folders-enabled"
                  checked={field.value ?? false}
                  onCheckedChange={(checked) =>
                    field.onChange(checked === true)}
                  disabled={isSaving}
                />
                <Label
                  htmlFor="tenant-folders-enabled"
                  className="text-sm font-normal cursor-pointer"
                >
                  {t('pages.tenants.form.foldersEnabledLabel')}
                </Label>
              </div>
            )}
          />
        </FormSection>
      </form>
    </EditPageLayout>
  );
}
